import {
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react'
import { DataTypeConfigUtils } from '@helo/cms'
import { Assert, Value, pipe } from '@helo/utils'
import { useMemo, useState } from 'react'

import { useAppStore } from '../../store/appStore/appStore'
import { Icon } from '../Icons/Icon'
import { LightModeModalContent } from '../Modal/LightModeModalContent'
import { showToast } from '../Toast/showToast'
import { Tooltip } from '../Tooltip/Tooltip'
import { ExportModalProps, createDefaultName } from './exportModal.parts'

export const ExportModal = ({
  queryId,
  title = 'List',
  exportTitle,
  isOpen = false,
  onOpen = () => {},
  onClose = () => {},
  columns,
  columnConfigs,
  connectionLocator,
  queryVariables,
  queryDefinitionId,
  renderTrigger,
  ...props
}: ExportModalProps) => {
  const queryNode = useAppStore(({ queryStore }) =>
    queryId ? queryStore.getQuery(queryId) : undefined,
  )

  // Get queryDefinition from queryNode to access the label for fallback
  const queryDefinition = useMemo(() => {
    if (!queryNode?.queryDataTypeConfig || !connectionLocator) {
      return undefined
    }

    try {
      return pipe(
        queryNode.queryDataTypeConfig,
        DataTypeConfigUtils.find(connectionLocator),
        Assert.orReturn(DataTypeConfigUtils.isKind('FIELD'), () => undefined),
      )
    } catch {
      return undefined
    }
  }, [queryNode?.queryDataTypeConfig, connectionLocator])

  // Apply fallback logic: title (caption) -> queryDefinition label -> 'export'
  const baseTitle =
    exportTitle || title || queryDefinition?.state.label || 'export'
  const effectiveTitle = `${baseTitle}`
  const defaultFileName = createDefaultName(effectiveTitle)

  // Only track user edits, default to computed filename
  const [userEditedFileName, setUserEditedFileName] = useState<string | null>(
    null,
  )
  const [exportRequested, setExportRequested] = useState<boolean>(false)

  // Use user edit if exists, otherwise use computed default
  const fileName = userEditedFileName ?? defaultFileName

  const closeHandler = () => {
    setExportRequested(false)
    setUserEditedFileName(null) // Reset user edits
    onClose()
  }

  return (
    <>
      <Tooltip label="Export" placement="top">
        {renderTrigger ? (
          renderTrigger
        ) : (
          <IconButton
            variant="controlButton"
            aria-label="Export"
            icon={<Icon icon="DownloadIcon" boxSize="16px" />}
            onClick={onOpen}
          />
        )}
      </Tooltip>
      <Modal isOpen={isOpen} onClose={closeHandler} size="xl">
        <ModalOverlay />
        <LightModeModalContent>
          <ModalHeader>Export File</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!exportRequested && (
              <RequestExportForm
                input={fileName}
                setFileName={setUserEditedFileName}
              />
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              bg="presets.turquoise.70"
              color="presets.neutral.0"
              padding="5px 15px"
              width="114px"
              height="40px"
              borderRadius="8px"
              fontStyle="normal"
              fontWeight="600"
              fontSize="14px"
              lineHeight="140%"
              letterSpacing="0.2px"
              mr={3}
              onClick={
                exportRequested
                  ? closeHandler
                  : () => {
                      useAppStore.getState().notifications.requestQueryExport({
                        queryDefinitionId: queryDefinitionId,
                        fileName,
                        variables: queryVariables,
                        columns: columns,
                        columnConfigs: columnConfigs,
                        connectionLocator: connectionLocator,
                      })
                      setExportRequested(true)
                      closeHandler()
                      showToast({
                        id: `view-export-toast-${fileName}`,
                        title: 'Export Started',
                        description: `Your export has started. You will receive a notification when it is ready.`,
                      })
                    }
              }
            >
              Begin Export
            </Button>
          </ModalFooter>
        </LightModeModalContent>
      </Modal>
    </>
  )
}

const RequestExportForm = ({
  input,
  setFileName,
}: {
  input: string
  setFileName: (name: string) => void
}) => {
  const handleInputChange = (e: { target: { value: string } }) =>
    setFileName(e.target.value)

  return (
    <FormControl>
      <FormLabel
        fontStyle="normal"
        fontWeight="500"
        fontSize="12px"
        lineHeight="15px"
        letterSpacing="0.2px"
        color="presets.neutral.80"
      >
        File Name
      </FormLabel>
      <Input
        padding="8px 4px 8px 8px"
        gap="4px"
        height="32px"
        background="presets.neutral.0"
        border="1px solid"
        borderColor="presets.neutral.30"
        borderRadius="6px"
        fontStyle="normal"
        fontWeight="500"
        fontSize="14px"
        lineHeight="140%"
        display="flex"
        letterSpacing="0.2px"
        color="presets.neutral.100"
        type="text"
        value={input}
        onChange={handleInputChange}
      />
    </FormControl>
  )
}
